import { useEffect, useRef } from 'react';
import { useCollection } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { IslideItem } from '@typings/setting/slideItem';

const CACHE_KEY = 'cache:slideList';
const CACHE_TTL_MS = 1000 * 60 * 30; // 30분

interface CachedSlideData {
  data: IslideItem[];
  timestamp: number;
}

function getSessionCache(): IslideItem[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed: CachedSlideData = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function setSessionCache(data: IslideItem[]) {
  try {
    const payload: CachedSlideData = { data, timestamp: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {}
}

/**
 * 슬라이드 목록을 sessionStorage 캐시 + SWR dedup으로 가져온다.
 * listen: false로 onSnapshot 대신 1회성 fetch → Firestore 읽기 절감.
 * 캐시가 있으면 즉시 반환하고, 백그라운드에서 revalidate.
 */
export default function useSlideList() {
  const sessionCache = useRef(getSessionCache());

  const { data: serverData } = useCollection<IslideItem>(
    `${CollectionPath.SLIDE}`,
    {
      listen: false,
      orderBy: ['order', 'asc'],
    },
  );

  useEffect(() => {
    if (serverData && serverData.length > 0) {
      setSessionCache(serverData);
    }
  }, [serverData]);

  const slideList = serverData ?? sessionCache.current ?? [];

  return slideList;
}
